import * as Err from '@fixtuur/error-context';
import { Array, Context, Effect, Layer, Record } from 'effect';
import * as Arr from 'fp-ts/Array';
import * as TE from 'fp-ts/TaskEither';
import { flow, pipe } from 'fp-ts/function';
import { iso } from 'newtype-ts';
import * as ApiParticle from '../../../api/configuration/particle';
import { ParticleService } from '../../../api/configuration/particle';
import * as Url from '../../../api/url';
import * as Raw from '../../../raw';
import * as AspectArticle from '../../aspect/article';
import * as AspectAttachments from '../../aspect/attachments';
import * as WithAttachments from './withAttachments';
// provides ability to wrap, unwrap, and modify the newtype
const _ = iso();
// Internal only
const fromWithAttachments = (serviceConfig, siblingWithAttachments, [id, attachments]) => ({
    id: id,
    attachmentTag: attachments.tag,
    modelUrl: ApiParticle.getModelUrl(serviceConfig, { id }),
    originDelta: attachments.originDelta,
    // we need to create a concrete slot for each attachment slot, per applicable tag which resolves to a particle, per attachment slot position
    slots: attachments.slots.flatMap(slot => slot.applicableTags
        // get a list of siblings that match applicable tags of the current Api slot (but aren't the current particle)
        .flatMap(tag => siblingWithAttachments.filter(([siblingId, siblingAttachments]) => tag === siblingAttachments.tag && siblingId !== id))
        // and construct a concrete Fixtuur slot for each position in the current Api slot
        .flatMap(([applicableParticle]) => slot.attachmentPoints.map(point => ({
        ...point,
        particleId: applicableParticle,
        name: slot.name,
        optional: !slot.onlyAttachmentPointOptionForApplicableTags,
    })))),
});
// export functions from here
export const create = _.wrap;
export var FromIdsErrorMessage;
(function (FromIdsErrorMessage) {
    FromIdsErrorMessage["FetchAttachmentData"] = "Error fetching particle attachment data from API";
})(FromIdsErrorMessage || (FromIdsErrorMessage = {}));
export const fromIds = (serviceConfig, ids) => {
    const createWithAttachments = (particles) => WithAttachments.fromPartices(serviceConfig, particles);
    return pipe(ids, Arr.map(ApiParticle.construct), createWithAttachments, TE.mapLeft(Err.swap(FromIdsErrorMessage.FetchAttachmentData)), 
    /** go through each individual WithAttachments and, using the other WAs too, build up a Particle. Maybe with a fromWA function that takes a full list and a current focus? */
    TE.map(allParticlesWithAttachments => {
        return pipe(allParticlesWithAttachments, Arr.map(currentParticleWithAttachments => {
            return fromWithAttachments(serviceConfig, allParticlesWithAttachments, currentParticleWithAttachments);
        }));
    }), TE.map(Arr.map(_.wrap)));
};
export const id = flow(_.unwrap, particle => particle.id);
export const attachmentTag = flow(_.unwrap, particle => particle.attachmentTag);
export const modelUrl = flow(_.unwrap, particle => particle.modelUrl);
export const originDelta = flow(_.unwrap, particle => particle.originDelta);
export const slots = flow(_.unwrap, particle => particle.slots);
/**
 * Assuming that the particles are from the same configuration, this function will return the root particle.
 *
 * The root particle is the particle that is not attached to any other particle.
 */
export const findRoot = (particles) => {
    const particleIdsInASlot = pipe(particles, Arr.chain(slots), Arr.map(slot => slot.particleId));
    return pipe(particles, Arr.findFirst(particle => {
        // if a particle isn't found in any other particle's slots, it's the root
        return !particleIdsInASlot.includes(id(particle));
    }));
};
export class AttachmentError {
    article;
    originalError;
    _tag = 'Fixtuur/Aspect/Particle/AttachmentError';
    constructor(article, originalError) {
        this.article = article;
        this.originalError = originalError;
    }
}
export class ArticleMissingFromPossibleChildrenError {
    slotName;
    _tag = 'Fixtuur/Aspect/Particle/ArticleMissingFromPossibleChildrenError';
    constructor(slotName) {
        this.slotName = slotName;
    }
}
export class FixtuurConfigurationParticleService extends Context.Tag('@fixtuur/backpack/FixtuurConfigurationParticleService')() {
}
export const BaseFixtuurConfigParticleLayer = Layer.effect(FixtuurConfigurationParticleService, Effect.gen(function* (_) {
    const articleService = yield* _(AspectArticle.FixtuurAspectArticleService);
    const particleService = yield* _(ParticleService);
    const fromAspectArticle = (article, { possibleChildren, handedness, tenantId }) => Effect.gen(function* (_) {
        const attachments = yield* _(articleService.attachments(tenantId, article), Effect.mapError(e => new AttachmentError(article, e)));
        const narrowedAttachments = yield* _(AspectAttachments.narrow(attachments, handedness).pipe(Effect.mapError(e => new AttachmentError(article, e))));
        const slots = yield* _(pipe(narrowedAttachments.attachments, Record.toEntries, Array.map(([slotName, attachment]) => {
            const article = possibleChildren.find(sel => sel.articleId === slotName);
            if (!article) {
                return Effect.fail(new ArticleMissingFromPossibleChildrenError(slotName));
            }
            const [x, y, z] = attachment.position.value();
            const [w, rx, ry, rz] = attachment.orientation.quaternion();
            const slot = {
                name: slotName,
                position: { x, y, z },
                orientation: { w, x: rx, y: ry, z: rz },
                particleId: AspectArticle.toString(article),
                optional: false,
            };
            return Effect.succeed(slot);
        }), Effect.all));
        return create({
            id: AspectArticle.toString(article),
            modelUrl: Url.toString(articleService.modelUrl(tenantId, article)),
            originDelta: {
                position: Raw.Position3D.fromFine(narrowedAttachments.originDelta.position),
                orientation: Raw.Orientation.fromFine(narrowedAttachments.originDelta.orientation),
            },
            slots,
            attachmentTag: article.articleId,
        });
    });
    const allParticles = tenant => particleService.getParticles(tenant);
    return { fromAspectArticle, allParticles };
}));
export const LiveFixtuurConfigurationParticleService = BaseFixtuurConfigParticleLayer.pipe(Layer.provide(AspectArticle.ToastFixtuurAspectArticleService), Layer.provide(ApiParticle.GluonParticleService));
export const fromAspectArticle = (article, context) => Effect.andThen(FixtuurConfigurationParticleService, s => s.fromAspectArticle(article, context));
